/**
 * This file contains code shared by Mimir Desktop and Mimir Adobe Panels in Premiere, AE, Photoshop.
 */
import type { PanelConfig } from '@mimir/web/desktop/storageSettings';
import { PREMIERE_STANDALONE_STORAGE_ID } from '@mimir/common/util/constants';
import { dirname, isDirAvailableLocally, readFile, readFileSuppressingErrors, recursiveMkdirSync, writeFile } from '@mimir/common/util/fs';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { getUserDataPath } from './utils';
import { ConsoleLogger } from '@mimir/common/util/logging';
import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import { desktopState } from '../globals/desktop';

export const DEFAULT_STORAGE_PROFILE_NAME = 'default';


export const initialConfig: PanelConfig = {
  selectedStorageProfileName: DEFAULT_STORAGE_PROFILE_NAME,
  availableStorageProfiles: {
    [DEFAULT_STORAGE_PROFILE_NAME]: {
      name: DEFAULT_STORAGE_PROFILE_NAME,
      isStandaloneMode: true,
      isProxyWorkflow: false,
      downloadPath: '',
      projectDownloadPath: '',
      storageId: PREMIERE_STANDALONE_STORAGE_ID,
      subclipExpansionRoom: 5,
    }
  },
  logLevel: 'info',
};


const appDataDirName = 'Mimir';
const configDirName = 'config';

// This used to return 'C:\\mimir-premiere-panel\\config' etc before Mimir Desktop. But apparently no customer has really
// been using system config, so we just changed this to return 'C:\\Mimir\\config' etc now, to have a more generic name.
export const getSystemConfigDir = (ctx: AdobePanelContext | ElectronContext) => {
  if (ctx.isWindows) {
    return `C:\\${appDataDirName}\\${configDirName}`;
  } else {
    return `/Library/Mjoll/${appDataDirName}/${configDirName}`;
  }
};

export function getSubDirInUserData(applicationContext: AdobePanelContext | ElectronContext, dirName: string): string {
  const userDataPath = getUserDataPath(applicationContext);
  const sep = applicationContext.pathSep;
  const mimirDataPath = `${userDataPath}${sep}${appDataDirName}`;
  const dirPath = `${mimirDataPath}${sep}${dirName}`;

  return dirPath;
}

// const getConfigDirInUserData = () => {
//   return getSubDirInUserData(configDirName);
// };

const getUserConfigPath = (applicationContext: AdobePanelContext | ElectronContext): string => {
  return getOldUserConfigPath(applicationContext);
  // const configDir = getConfigDirInUserData();
  // const configPath = `${configDir}${getSep()}config.json`;
  // return configPath;
};

// returns the user config path used by Adobe panel before we started to use a common config for Mimir Desktop and Adobe panel
const getOldUserConfigPath = (applicationContext: AdobePanelContext | ElectronContext): string => {
  const userDataPath = getUserDataPath(applicationContext);
  const sep = applicationContext.pathSep;
  const mimirPanelConfigDirPath = `${userDataPath}${sep}mimir-premiere-panel${sep}config`;
  const configPath = `${mimirPanelConfigDirPath}${sep}config.json`;
  return configPath;
};

const getSystemConfigPath = (applicationContext: AdobePanelContext | ElectronContext): string => {
  const configDir = getSystemConfigDir(applicationContext);
  const configPath = `${configDir}${applicationContext.pathSep}config.json`;
  return configPath;
};

// If not specified, the default should be to allow override. restricting override needs to be specified explicitly
export const shouldDisableOverride = (config: PanelConfig | undefined) => {
  return config?.disableOverride ?? false;
};

export const sanitizePanelConfig = (inputConfig: PanelConfig | undefined): PanelConfig | undefined => {
  const config = cloneDeep(inputConfig);
  if (!config || isEmpty(config) || isEmpty(config.availableStorageProfiles)) {
    return config;
  }

  for (const profile of Object.values(config.availableStorageProfiles)) {
    if (profile.isStandaloneMode) {
      profile.storageId = PREMIERE_STANDALONE_STORAGE_ID;
    } else if (profile.storageId === PREMIERE_STANDALONE_STORAGE_ID) {
      delete profile.storageId;
    }

    if (!profile.projectDownloadPath) {
      profile.projectDownloadPath = '';
    }

    if (typeof profile.subclipExpansionRoom !== 'number' || profile.subclipExpansionRoom < 0 || profile.subclipExpansionRoom > 60) {
      profile.subclipExpansionRoom = 5;
    }
  }

  return config;
};

/**
 * Tries to get system config first and user config next. User config takes precedence if not disabled from systemConfig.
 * While getting user config, it looks in the new location (Mimir folder) and uses that if available. If not, it checks
 * the old location (mimir-premiere-panel folder), and copies it to the new location if available. This way, going forward,
 * we migrate existing panel config to the new location and share that among Mimir Desktop and Adobe panels.
 */
export const readPanelConfig = async (applicationContext: AdobePanelContext | ElectronContext): Promise<PanelConfig | undefined> => {
  let systemConfig: PanelConfig | undefined = undefined;

  try {
    const systemConfigPath = getSystemConfigPath(applicationContext);
    const systemConfigString = readFileSuppressingErrors(applicationContext.nodeApi, systemConfigPath) as string;
    if (!isEmpty(systemConfigString)) {
      systemConfig = sanitizePanelConfig(JSON.parse(systemConfigString!));
    } else {
      console.log('system config not available');
    }
  } catch (err) {
    console.error(`Error getting system config: ${err}`);
    systemConfig = undefined;
  }

  // at this point, systemConfig is either not available, or it allows override

  const userConfigPath = getUserConfigPath(applicationContext);

  // const configAvailable = await isFileAvailableLocally(userConfigPath);
  // if (!configAvailable) {
  //   // new config location not found. check and migrate from old user config (possibly added by Premiere panel) if possible
  //   const oldUserConfigPath = getOldUserConfigPath();
  //   console.log(`oldUserConfigPath: ${oldUserConfigPath}`);

  //   const oldConfigAvailable = await isFileAvailableLocally(oldUserConfigPath);
  //   if (oldConfigAvailable) {
  //     try {
  //       // so, this machine ran mimir-premiere-panel sometime in the past. we should copy the old files.
  //       recursiveMkdirSync(dirname(userConfigPath));
  //       await copyFile(oldUserConfigPath, userConfigPath);

  //       // we can deprecate the old file by renaming/deleting it. but chose not to do it now since it can result
  //       // in a lot of confusion during testing and before we have finalized Mimir Desktop for release. For now,
  //       // we are just leaving the old file alone.
  //       // await rename(oldUserConfigPath, `${oldUserConfigPath}.old`);
  //     } catch (err) {
  //       console.error(`Error migrating from old config at ${oldUserConfigPath} to new config at ${userConfigPath}. Error: ${err}`);
  //     }
  //   }
  // }

  console.log(`userConfigPath: ${userConfigPath}`);

  // now read the actual user config file
  let userConfig: PanelConfig | undefined;
  try {
    const userConfigString = readFile(applicationContext.nodeApi, userConfigPath);
    if (!isEmpty(userConfigString)) {
      userConfig = sanitizePanelConfig(JSON.parse(userConfigString!));
    }
  } catch (err) {
    console.error(`Error getting user config: ${err}`);
    userConfig = undefined;
  }


  if (systemConfig) {
    if (shouldDisableOverride(systemConfig)) {
      const extraLogLinesArr: string[] = [];
      if (userConfig?.selectedStorageProfileName && userConfig?.selectedStorageProfileName !== systemConfig.selectedStorageProfileName) {
        extraLogLinesArr.push(`selectedStorageProfileName overridden from "${systemConfig.selectedStorageProfileName}" to "${userConfig.selectedStorageProfileName}"`);
        systemConfig.selectedStorageProfileName = userConfig.selectedStorageProfileName;
      }
      if (userConfig?.selectedPresetPath && userConfig?.selectedPresetPath !== systemConfig.selectedPresetPath) {
        extraLogLinesArr.push(`selectedPresetPath overridden from "${systemConfig.selectedPresetPath}" to "${userConfig.selectedPresetPath}"`);
        systemConfig.selectedPresetPath = userConfig.selectedPresetPath;
      }

      // systemConfig should be used when it does not allow override
      const extraLogLines = extraLogLinesArr.length ? `${extraLogLinesArr.join('\n')}\n` : '';
      console.log(`system config enforced\n${extraLogLines}${JSON.stringify(systemConfig, null, 2)}`);
      return systemConfig;
    } else {
      console.log('system config found but not enforced');
    }
  }

  if (userConfig) {
    // userConfig should be used when systemConfig is not available or override is allowed
    console.log(`user config found: ${JSON.stringify(userConfig, null, 2)}`);
  } else if (isEmpty(systemConfig)) {
    // when userConfig is empty, fall back to systemConfig
    userConfig = initialConfig;
    console.log(`user config initialized: ${JSON.stringify(userConfig, null, 2)}`);
  } else {
    userConfig = cloneDeep(systemConfig);
    // userConfig should reset disableOverride
    userConfig!.disableOverride = undefined;
    console.log(`user config initialized from system config: ${JSON.stringify(userConfig, null, 2)}`);
  }

  return userConfig;
};

export const writePanelConfig = async (applicationContext: AdobePanelContext | ElectronContext, inputConfig: PanelConfig) => {
  const config = sanitizePanelConfig(inputConfig);

  if (!config) {
    return;
  }
  if (config.disableOverride === true) {
    console.debug('Saving user-specific panel config without disableOverride option');
    delete config.disableOverride;
  }

  const configPath = getUserConfigPath(applicationContext);

  // create directories of the path first, if needed
  const configDir = dirname(applicationContext.nodeApi, configPath);
  if (!await isDirAvailableLocally(applicationContext.nodeApi, new ConsoleLogger(), configDir)) {
    recursiveMkdirSync(applicationContext.nodeApi, configDir);
  }

  config.logLevel = desktopState.logLevel;
  const content = JSON.stringify(config, null, 2);
  writeFile(applicationContext.nodeApi, configPath, content);
};
